import {
  INotificationResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/INotificationResponseInstance'
import {NotificationType} from '@toolify/server/src/models/mongoose/NotificationModel/enum/NotificationType'
import {INotificationItem} from '@toolify/client/src/modules/notifications/types/INotificationItem'
import {NotificationTypeItemMap} from '../maps/NotificationTypeItemMap'
import {LogItemTitleItemType} from '@toolify/client/src/modules/activity/enum/LogItemTitleItemType'
import {LogTitleItem} from '@toolify/client/src/modules/activity/types/ILogTitleItem'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {getNotificationDetailsRoute} from './getNotificationDetailsRoute'
import {i18n} from '@toolify/client/src/setup/i18n'
import * as Sentry from '@sentry/vue'

const {t} = i18n.global

const ITEMS_COUNT_AGGREGATION_NOTIFICATION_TYPES = new Set<NotificationType>([
  NotificationType.UserAssignedToDatabaseItem,
  NotificationType.UserAssigneeChangedOnDatabaseItem,
  NotificationType.StatusChangedOnDatabaseItem,
  NotificationType.LabelChangedOnDatabaseItem,
  NotificationType.DatabaseItemDateChanged,
  NotificationType.DatabaseItemCreated,
])

const FILES_COUNT_AGGREGATION_NOTIFICATION_TYPES = new Set<NotificationType>([
  NotificationType.DatabaseItemFileCreated,
  NotificationType.DatabaseItemFileDeleted,
  NotificationType.CloudItemCreated,
  NotificationType.CloudItemDeleted,
])

function extractNotificationTypeItem(
  notification: INotificationResponseInstance<NotificationType>,
): Partial<INotificationItem<NotificationType>> {
  const notificationTypeItem = NotificationTypeItemMap.get(notification.type)
  if (!notificationTypeItem) {
    return {}
  }
  try {
    return notificationTypeItem(notification) || {}
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        notificationId: notification.id,
        notificationType: notification.type,
      },
    })
    return {}
  }
}

export function mapNotificationToItem(notification: INotificationResponseInstance<NotificationType>): INotificationItem | null {
  // A type this build does not know (newer server during a rolling deploy, or data written by a
  // different version) must still render — dropping it desyncs the list from the unread badge
  const extractedNotificationItem = extractNotificationTypeItem(notification)
  const titleItems: LogTitleItem[] = [
    {
      type: LogItemTitleItemType.Entity,
      payload: notification.authorEntity,
    },
    {
      type: LogItemTitleItemType.String,
      payload: t('notifications.notificationItemTitleItems.performedAnAction'),
    },
  ]

  const notificationItem: INotificationItem = {
    primaryIconClass: IconNameType.Bell,
    secondaryIconClass: '',
    titleItems,
    isRead: notification.readAt !== null,
    id: notification.id,
    aggregationKey: notification.aggregationKey,
    authorEntity: notification.authorEntity,
    createdAt: notification.createdAt,
    onAfterRead: async () => {
      return
    },
    detailsTo: getNotificationDetailsRoute(notification),
    resourcePath: notification.resourcePath,
    ...extractedNotificationItem,
    viaEntity: notification.viaEntity ?? extractedNotificationItem.viaEntity ?? null,
  }

  if (!notificationItem.to) {
    notificationItem.to = notificationItem.detailsTo
  }

  // eventCount survives reading the group, so a read group still shows what it is made of
  const groupEventCount = notification.eventCount ?? notification.unreadGroupCount ?? 0
  notificationItem.groupEventCount = groupEventCount
  if (groupEventCount > 1) {
    let groupCountTranslationKey: string = null
    if (FILES_COUNT_AGGREGATION_NOTIFICATION_TYPES.has(notification.type)) {
      groupCountTranslationKey = 'notifications.notificationItemTitleItems.moreFilesCount'
    } else if (ITEMS_COUNT_AGGREGATION_NOTIFICATION_TYPES.has(notification.type)) {
      // Per-(item, property) sessions describe repeated changes of ONE value (the row shows the
      // last state); teleport-keyed groups deliberately span many items
      groupCountTranslationKey = notification.aggregationKey?.startsWith('item-value:')
        ? 'notifications.notificationItemTitleItems.moreChangesCount'
        : 'notifications.notificationItemTitleItems.moreItemsCount'
    }
    if (groupCountTranslationKey) {
      notificationItem.titleItems = [
        ...notificationItem.titleItems,
        {
          type: LogItemTitleItemType.String,
          payload: t(groupCountTranslationKey, {count: groupEventCount}),
        },
      ]
      notificationItem.isAggregationGroup = true
    }
  }

  return notificationItem
}
